import { useState, useEffect, useRef } from "react";
import { Trash2, Pencil, Plus } from "lucide-react";
import { Button, Modal, Table } from "../../../components";
import axios from "axios";
import { GET_USERS_URL, DELETES_USER_URL } from "../../../features/UrlHelper";
import { handleApiError } from "../../../features/errorHandler";
import {
  POSTAL_CODE_PATTERN,
  POSTAL_CODE_TITLE,
  STREET_PATTERN,
  STREET_TITLE,
  formatAddress,
  isAddressComplete,
} from "../../../features/address";
import { PHONE_PATTERN, PHONE_TITLE } from "../../../features/phone";
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_MIN_LENGTH_MESSAGE,
} from "../../../features/password";

function UsersSection() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Add / Edit modal
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userSaving, setUserSaving] = useState(false);
  const [userFormError, setUserFormError] = useState("");
  const userFormErrorRef = useRef(null);
  const [userForm, setUserForm] = useState({
    id: null,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    postalCode: "",
    city: "",
    plan_name: "",
    valid_from: "",
    valid_to: "",
    is_verified: false,
    discount: 0,
    password: "",
    role: "user",
  });

  var today = new Date();

  useEffect(() => {
    loadUsers();
  }, []);

  // Chyba zo servera je pod poľami – na menšej obrazovke ju treba posunúť do zorného poľa
  useEffect(() => {
    if (userFormError) {
      userFormErrorRef.current?.scrollIntoView({ block: "nearest" });
    }
  }, [userFormError]);

  async function loadUsers() {
    try {
      const response = await axios.get(GET_USERS_URL, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
        },
      });
      setUsers(response.data);
    } catch (error) {
      handleApiError(error, "Nepodarilo sa načítať používateľov.");
    } finally {
      setLoadingUsers(false);
    }
  }

  const openAddUser = () => {
    setEditingUser(null);
    setUserForm({
      id: null,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      street: "",
      postalCode: "",
      city: "",
      plan_name: "",
      valid_from: "",
      valid_to: "",
      is_verified: false,
      discount: 0,
      password: "",
      role: "user",
    });
    setUserFormError("");
    setUserModalOpen(true);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      id: user.id,
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phone: user.phone || "",
      street: user.street || "",
      postalCode: user.postalCode || "",
      city: user.city || "",
      plan_name: user.plan_name || "",
      valid_from: user.valid_from ? user.valid_from.slice(0, 10) : "",
      valid_to: user.valid_to ? user.valid_to.slice(0, 10) : "",
      is_verified: user.is_verified || false,
      discount: user.discount ?? 0,
      password: "", // Leave password empty when editing, only update if a new password is entered
      role: user.role || "user",
    });
    setUserFormError("");
    setUserModalOpen(true);
  };

  const handleUserSubmit = (e) => {
    e.preventDefault();
    const data = {
      id: userForm.id,
      firstName: userForm.firstName,
      lastName: userForm.lastName,
      email: userForm.email,
      phone: userForm.phone,
      street: userForm.street,
      postalCode: userForm.postalCode,
      city: userForm.city,
      plan_name: userForm.plan_name,
      valid_from: userForm.valid_from,
      valid_to: userForm.valid_to,
      is_verified: userForm.is_verified,
      discount: parseInt(userForm.discount, 10) || 0,
      password: userForm.password, // Include password for both create and update (backend should handle it accordingly)
      role: userForm.role,
    };

    const config = {
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
      },
    };
    // Úprava = POST, nový používateľ = PUT
    const request = editingUser
      ? axios.post(GET_USERS_URL, data, config)
      : axios.put(GET_USERS_URL, data, config);

    // Modal ostáva otvorený, kým server údaje neprijme – pri chybe validácie sa nestratia
    setUserFormError("");
    setUserSaving(true);
    request
      .then(() => {
        setUserModalOpen(false);
        loadUsers();
      })
      .catch((error) =>
        setUserFormError(
          handleApiError(
            error,
            editingUser
              ? "Nepodarilo sa upraviť používateľa."
              : "Nepodarilo sa vytvoriť používateľa."
          )
        )
      )
      .finally(() => setUserSaving(false));
  };

  const deleteUser = (id) => {
    //api call to delete user
    axios.delete(DELETES_USER_URL(id), {
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
      },
    })
      .then(() => loadUsers())
      .catch((error) => handleApiError(error, { showAlert: true, fallbackMessage: "Nepodarilo sa zmazať používateľa." }));
    setDeleteConfirm(null);
  };

  const nameStatus = (user) => {
    if (user.valid_from && new Date(user.valid_from) > today) {
      return (
        "Aktívne od " + new Date(user.valid_from).toLocaleDateString("sk-SK")
      );
    } else if (
      user.valid_from &&
      new Date(user.valid_from) <= today &&
      user.valid_to &&
      new Date(user.valid_to) >= today
    ) {
      return "Aktívne";
    } else {
      return "Neaktívne";
    }
  };

  const isSubscriptionActive = (user) =>
    user.valid_from &&
    new Date(user.valid_from) <= today &&
    new Date(user.valid_to) >= today;

  // Dátum sa v bunke nesmie zalomiť ("1. 3." / "2025")
  const formatDate = (val) => (
    <span className="admin__nowrap">
      {val ? new Date(val).toLocaleDateString("sk-SK") : "-"}
    </span>
  );

  // Zlúčené stĺpce, aby sa tabuľka zmestila na šírku – Table hľadá aj triedi podľa row[key]
  const tableRows = users.map((user) => ({
    ...user,
    fullName: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
    contact: `${user.email || ""} ${user.phone || ""}`,
    address: formatAddress(user),
    // Začína dátumom konca platnosti (ISO) → triedi sa podľa platnosti, hľadá sa aj plán a stav
    subscription: `${user.valid_to || ""} ${user.plan_name || ""} ${nameStatus(user)}`,
  }));

  return (
    <div className="admin__section">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Správa užívateľov</h2>
        <Button variant="primary" size="sm" onClick={openAddUser}>
          <Plus size={16} /> Pridať užívateľa
        </Button>
      </div>

      <div className="admin__table-wrapper">
        <Table
          className="admin__users-table"
          columns={[
            {
              key: "fullName",
              label: "Meno",
              render: (val, row) => (
                <div>
                  <div className="admin__user-name">{val || "—"}</div>
                  {!row.is_verified && (
                    <div className="admin__user-email">
                      <span className="admin__status-badge admin__nowrap">
                        Neoverený
                      </span>
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: "contact",
              label: "Kontakt",
              render: (_, row) => (
                <div>
                  <div
                    className="admin__table-ellipsis admin__table-ellipsis--email"
                    title={row.email}
                  >
                    {row.email}
                  </div>
                  <div className="admin__user-email admin__nowrap">
                    {row.phone || (
                      <span className="admin__status-badge">Chýba telefón</span>
                    )}
                  </div>
                </div>
              ),
            },
            {
              key: "address",
              label: "Adresa",
              render: (_, row) =>
                isAddressComplete(row) ? (
                  <div>
                    <div>{row.street}</div>
                    <div className="admin__user-email">
                      <span className="admin__nowrap">{row.postalCode}</span>{" "}
                      {row.city}
                    </div>
                  </div>
                ) : (
                  <span className="admin__status-badge">Chýba</span>
                ),
            },
            {
              key: "discount",
              label: "Zľava",
              render: (val) => <span className="admin__nowrap">{val ?? 0} %</span>,
            },
            {
              key: "subscription",
              label: "Predplatné",
              render: (_, row) => (
                <div>
                  <span
                    className={`admin__status-badge admin__nowrap ${isSubscriptionActive(row) ? "admin__status-badge--active" : ""}`}
                  >
                    {nameStatus(row)}
                  </span>
                  {row.valid_from && (
                    <div className="admin__user-email">
                      {row.plan_name && `${row.plan_name} · `}
                      {formatDate(row.valid_from)} – {formatDate(row.valid_to)}
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: "created_at",
              label: "Registrácia",
              render: formatDate,
            },
            {
              key: "actions",
              label: "Akcie",
              sortable: false,
              render: (_, row) => (
                <div className="admin__table-actions">
                  <button className="admin__icon-btn" onClick={() => openEditUser(row)} title="Upraviť">
                    <Pencil size={16} />
                  </button>
                  {row.role != "admin" && (
                    <button className="admin__icon-btn admin__icon-btn--danger" onClick={() => setDeleteConfirm(row.id)} title="Zmazať">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
          data={tableRows}
          searchPlaceholder="Hľadať užívateľa..."
          emptyMessage="Žiadni užívatelia"
        />
      </div>

      {/* Add / Edit user modal */}
      <Modal
        isOpen={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        title={editingUser ? "Upraviť užívateľa" : "Pridať užívateľa"}
      >
        <form
          className="admin__form admin__form--compact"
          onSubmit={handleUserSubmit}
        >
          <div className="admin__form-row">
            <div className="admin__form-field">
              <label className="admin__form-label">Meno</label>
              <input
                maxLength={255}
                className="admin__form-input"
                value={userForm.firstName}
                onChange={(e) =>
                  setUserForm((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
                placeholder="Meno"
                required
              />
            </div>
            <div className="admin__form-field">
              <label className="admin__form-label">Priezvisko</label>
              <input
                maxLength={255}
                className="admin__form-input"
                value={userForm.lastName}
                onChange={(e) =>
                  setUserForm((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }))
                }
                placeholder="Priezvisko"
                required
              />
            </div>
          </div>
          <div className="admin__form-row admin__form-row--stack">
            <div className="admin__form-field">
              <label className="admin__form-label">Email</label>
              <input
                maxLength={255}
                type="email"
                name="email"
                className="admin__form-input"
                value={userForm.email}
                onChange={(e) =>
                  setUserForm((prev) => ({ ...prev, email: e.target.value }))
                }
                placeholder="email@example.com"
                required
                disabled={!!editingUser} // Disable email editing when updating user
                title={userForm.email}
                autoComplete="username"
              />
            </div>
            <div className="admin__form-field">
              <label className="admin__form-label">Telefón</label>
              <input
                maxLength={20}
                type="tel"
                className="admin__form-input"
                value={userForm.phone}
                onChange={(e) =>
                  setUserForm((prev) => ({ ...prev, phone: e.target.value }))
                }
                placeholder="0905 123 456"
                pattern={PHONE_PATTERN}
                title={PHONE_TITLE}
                required
              />
            </div>
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Ulica a číslo domu</label>
            <input
              maxLength={255}
              className="admin__form-input"
              value={userForm.street}
              onChange={(e) =>
                setUserForm((prev) => ({ ...prev, street: e.target.value }))
              }
              placeholder="Hlavná 12"
              pattern={STREET_PATTERN}
              title={STREET_TITLE}
              required
            />
          </div>
          <div className="admin__form-row">
            <div className="admin__form-field">
              <label className="admin__form-label">PSČ</label>
              <input
                maxLength={6}
                inputMode="numeric"
                className="admin__form-input"
                value={userForm.postalCode}
                onChange={(e) =>
                  setUserForm((prev) => ({
                    ...prev,
                    postalCode: e.target.value,
                  }))
                }
                placeholder="831 06"
                pattern={POSTAL_CODE_PATTERN}
                title={POSTAL_CODE_TITLE}
                required
              />
            </div>
            <div className="admin__form-field">
              <label className="admin__form-label">Mesto</label>
              <input
                maxLength={100}
                className="admin__form-input"
                value={userForm.city}
                onChange={(e) =>
                  setUserForm((prev) => ({ ...prev, city: e.target.value }))
                }
                placeholder="Bratislava"
                required
              />
            </div>
          </div>
          <div className="admin__form-row">
            <div className="admin__form-field">
              <label className="admin__form-label">Heslo</label>
              <input
                maxLength={128}
                type="password"
                name="new-password"
                className="admin__form-input"
                value={userForm.password || ""}
                onChange={(e) =>
                  setUserForm((prev) => ({ ...prev, password: e.target.value }))
                }
                placeholder={
                  editingUser
                    ? "Bez zmeny"
                    : `Min. ${PASSWORD_MIN_LENGTH} znakov`
                }
                minLength={PASSWORD_MIN_LENGTH}
                title={PASSWORD_MIN_LENGTH_MESSAGE}
                required={!editingUser}
                autoComplete="new-password"
              />
            </div>
            <div className="admin__form-field">
              <label className="admin__form-label">Zľava (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                className="admin__form-input"
                value={userForm.discount !== undefined && userForm.discount !== null ? userForm.discount : 0}
                onChange={(e) =>
                  setUserForm((prev) => ({
                    ...prev,
                    discount: e.target.value === "" ? "" : parseInt(e.target.value, 10) || 0,
                  }))
                }
                placeholder="0"
              />
            </div>
          </div>

          {userFormError && (
            <div
              className="admin__status admin__status--error"
              ref={userFormErrorRef}
            >
              {userFormError}
            </div>
          )}

          <div className="admin__form-actions">
            <label className="admin__form-field-checkbox admin__form-actions-start">
              <input
                type="checkbox"
                className="admin__form-checkbox"
                checked={userForm.is_verified || false}
                onChange={(e) =>
                  setUserForm((prev) => ({
                    ...prev,
                    is_verified: e.target.checked,
                  }))
                }
              />
              <span className="admin__form-label">Overený</span>
            </label>
            <Button
              variant="ghost"
              type="button"
              onClick={() => setUserModalOpen(false)}
            >
              Zrušiť
            </Button>
            <Button variant="primary" type="submit" disabled={userSaving}>
              {userSaving
                ? "Ukladám..."
                : editingUser
                  ? "Uložiť zmeny"
                  : "Pridať užívateľa"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Potvrdiť zmazanie"
      >
        <p>Naozaj chcete zmazať tohto užívateľa? Táto akcia sa nedá vrátiť.</p>
        <div className="admin__form-actions">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Zrušiť
          </Button>
          <Button
            variant="primary"
            onClick={() => deleteUser(deleteConfirm)}
            style={{ backgroundColor: "#EF4444", borderColor: "#EF4444" }}
          >
            Zmazať
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default UsersSection;
